import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, TextField, Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import { resetPassword } from '../../services/userService';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [serviceError, setServiceError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (currentData) => {
    const errors = {
      newPassword: '',
      confirmPassword: ''
    };

    if (!currentData.newPassword?.trim()) {
      errors.newPassword = 'Required';
    }

    if (!currentData.confirmPassword?.trim()) {
      errors.confirmPassword = 'Required';
    } else if (currentData.newPassword !== currentData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const hasValidationErrors = (errors) => {
    return Object.values(errors).some((error) => !!error);
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    const updatedData = {
      ...formData,
      [name]: value
    };

    setFormData(updatedData);
    setFieldErrors(validateForm(updatedData));
    setServiceError('');
    setSuccessMessage('');
  };

  const handleResetPassword = async () => {
    const errors = validateForm(formData);
    setFieldErrors(errors);

    if (hasValidationErrors(errors) || !token) {
      return;
    }

    try {
      setIsSubmitting(true);
      setServiceError('');
      await resetPassword({ token, newPassword: formData.newPassword });
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
    } catch (error) {
      setServiceError('Sorry, an error occurred while resetting your password.');
      setSuccessMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formHasErrors = hasValidationErrors(fieldErrors);

  return (
    <Box sx={{ maxWidth: 520, margin: '0 auto', p: 2 }} data-testid="reset-password-page">
      <Typography variant="h5" gutterBottom>
        Reset Password
      </Typography>

      {!successMessage && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your new password to continue.
          </Typography>

          <TextField
            fullWidth
            margin="normal"
            label="New Password"
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handlePasswordChange}
            error={!!fieldErrors.newPassword}
            helperText={fieldErrors.newPassword}
            inputProps={{ 'data-testid': 'reset-password-new-input' }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Confirm Password"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handlePasswordChange}
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword}
            inputProps={{ 'data-testid': 'reset-password-confirm-input' }}
          />
        </>
      )}

      {!successMessage && (
        <Button
          variant="contained"
          sx={{ mt: 2, textTransform: 'none' }}
          onClick={handleResetPassword}
          disabled={isSubmitting || formHasErrors || !formData.newPassword || !formData.confirmPassword || !token}
          data-testid="reset-password-submit-button"
        >
          {isSubmitting ? 'Resetting...' : 'Reset Password'}
        </Button>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }} data-testid="reset-password-success-alert">
          {successMessage}
        </Alert>
      )}

      {serviceError && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="reset-password-service-error-alert">
          {serviceError}
        </Alert>
      )}

      {!token && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }} data-testid="reset-password-token-error">
          Invalid or missing reset token.
        </Typography>
      )}

      <Typography variant="body2" sx={{ mt: 2 }}>
        <RouterLink to="/login" data-testid="reset-password-back-link">Back to login</RouterLink>
      </Typography>
    </Box>
  );
}
